import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, Paper, Fade } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { useTheme } from '@mui/material/styles';

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: Event) => void;
  onend: () => void;
}

interface SpeechRecognitionEvent extends Event {
  results: {
    [index: number]: {
      isFinal: boolean;
      [index: number]: {
        transcript: string;
      };
      length: number;
    };
    length: number;
  };
}

// Class teacher data
const classTeachers: { [key: string]: string } = {
  'I': 'Mrs. Smarmine Fernandez',
  'II-A': 'Mrs. Jacklyn',
  'II-B': 'Mrs. Alice',
  'III-A': 'Mrs. Usha',
  'III-B': 'Mrs. Rochal',
  'IV-A': 'Mrs. Jemima',
  'IV-B': 'Mrs. Deisy',
  'V-A': 'Mrs. Rebecca Maria Sam',
  'V-B': 'Mrs. Mary Silvia',
  'VI-A': 'Mrs. Beulah S.',
  'VI-B': 'Mrs. Prabha',
  'VII-A': 'Mrs. Beaula J.',
  'VII-B': 'Mrs. Thabitha Victoria Getsia',
  'VIII-A': 'Mrs. Charmaine Joseph',
  'VIII-B': 'Mr. Irudhayaraj J.',
  'VIII-C': 'Mrs. Angelin Sopiah',
  'IX-A': 'Mrs. Caroline Devak Trubai',
  'IX-B': 'Mrs. Mary Karnap',
  'X-A': 'Mr. Sambabu',
  'X-B': 'Mrs. Leena',
  'XI-A': 'Mr. Abraham Jai Singh',
  'XI-B': 'Mrs. Reena Jayakumari',
  'XII-A': 'Mr. John Soloman',
  'XII-B': 'Mr. Pradeep Celestine Charles'
};

const VoiceAssistant: React.FC = () => {
  const [message, setMessage] = useState('Click the mic to ask about class teachers');
  

  // 1. Update the state at the top of the component
const [messages, setMessages] = useState<Array<{text: string, isUser: boolean}>>([]);
const [isListening, setIsListening] = useState(false);
const [isOpen, setIsOpen] = useState(false);
const [isProcessing, setIsProcessing] = useState(false);
const recognitionRef = useRef<SpeechRecognition | null>(null);
const theme = useTheme();
const containerRef = useRef<HTMLDivElement>(null);
const [isClosing, setIsClosing] = useState(false);


  // Update the state and toggle function
const [isChatOpen, setIsChatOpen] = useState(false);

const toggleChat = useCallback(() => {
  setIsChatOpen(prev => !prev);
}, []);

  const speak = useCallback((text: string, onEnd?: () => void) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      if (onEnd) {
        utterance.onend = onEnd;
      }
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  const processVoiceCommand = useCallback((command: string): string => {
    const lowerCommand = command.toLowerCase();
    const classMatch = lowerCommand.match(/(\d+)(?:\s*[a-z])?/);
    
    if (classMatch) {
      const classNum = classMatch[1];
      const sectionMatch = lowerCommand.match(/[a-z]$/);
      const section = sectionMatch ? sectionMatch[0].toUpperCase() : '';
      
      let classId = '';
      const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
      
      if (parseInt(classNum) >= 1 && parseInt(classNum) <= 12) {
        classId = romanNumerals[parseInt(classNum) - 1];
        if (section) {
          classId += `-${section}`;
        }
      } else {
        return `I can only provide information for classes I through XII.`;
      }
      
      if (section) {
        if (classTeachers[classId]) {
          return `The class teacher of ${classId} is ${classTeachers[classId]}.`;
        } else {
          const availableSections = Object.keys(classTeachers)
            .filter(cls => cls.startsWith(classId.split('-')[0]))
            .map(cls => cls.split('-')[1] || 'No Section');
            
          return `I couldn't find section ${section.toUpperCase()} for class ${classId.split('-')[0]}. ` +
                 `Available sections: ${availableSections.join(', ')}`;
        }
      } else {
        const sections = Object.keys(classTeachers)
          .filter(cls => cls.startsWith(classId))
          .map(cls => ({
            id: cls,
            teacher: classTeachers[cls]
          }));
          
        if (sections.length > 0) {
          if (sections.length === 1) {
            return `The class teacher of ${sections[0].id} is ${sections[0].teacher}.`;
          } else {
            return `${classId} has multiple sections. ` +
                   sections.map(s => `\n${s.id}: ${s.teacher}`).join('');
          }
        } else {
          return `I couldn't find information for class ${classId}.`;
        }
      }
    }
    
    if (lowerCommand.includes('hello') || lowerCommand.includes('hi')) {
      return 'Hello! You can ask me about class teachers. For example, "Who is the teacher for class 2 A?"';
    } else if (lowerCommand.includes('thank')) {
      return 'You\'re welcome! Is there another class you\'d like to know about?';
    } else if (lowerCommand.includes('help')) {
      return 'You can ask me about class teachers. For example: "Who is the teacher for class 5 B?" or "Who teaches class 8?"';
    }
    
    return 'I can help you find class teachers. Try asking something like "Who is the teacher for class 3 A?"';
  }, []);

const handleResult = useCallback((event: SpeechRecognitionEvent) => {
  if (isProcessing) return;
  
  setIsProcessing(true);
  try {
    const results = event.results;
    const lastResult = results[results.length - 1];
    const transcript = lastResult[0].transcript;
    
    if (transcript.trim()) {
      // Add user message
      setMessages(prev => [...prev, { text: transcript, isUser: true }]);
      
      // Process the command
      const response = processVoiceCommand(transcript);
      
      // Add assistant response
      setMessages(prev => [...prev, { text: response, isUser: false }]);
      
      // Speak the response
      speak(response, () => {
        setIsProcessing(false);
      });
    } else {
      setIsProcessing(false);
    }
  } catch (error) {
    console.error('Error processing speech result:', error);
    setIsProcessing(false);
  }
}, [isProcessing, processVoiceCommand, speak]);

  const handleError = useCallback((event: any) => {
    console.error('Speech recognition error', event.error);
    setMessage('Error: Could not understand. Please try again.');
    setIsListening(false);
    setIsProcessing(false);
  }, []);

  const handleEnd = useCallback(() => {
    if (isListening && recognitionRef.current) {
      recognitionRef.current.start();
    }
  }, [isListening]);

const closePopup = useCallback(() => {
  if (isListening) {
    recognitionRef.current?.stop();
    setIsListening(false);
  }
  setIsOpen(false);
  // Don't clear messages here to keep the conversation history
}, [isListening]);

  useEffect(() => {
  const handleClosePopup = () => {
    if (!isListening && isOpen) {
      // Only close if we're not currently listening and the popup is open
      setIsOpen(false);
    }
  };

  const timer = setTimeout(handleClosePopup, 100); // Small delay to ensure state updates
  return () => clearTimeout(timer);
}, [isListening, isOpen]);


  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new (window as any).webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.lang = 'en-IN';

      recognition.onresult = handleResult;
      recognition.onerror = handleError;
      recognition.onend = handleEnd;

      recognitionRef.current = recognition;

      return () => {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      };
    }
  }, [handleResult, handleError, handleEnd]);

  // Add this new useEffect for click-outside handling
// Add this effect to handle clicks outside the voice assistant
useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (!containerRef.current) return;
    const target = event.target as Node;
    
    if (!containerRef.current.contains(target)) {
      closePopup();
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, [closePopup]);

const toggleListening = useCallback(() => {
  if (isProcessing) return;
  
  if (!isListening) {
    // Start listening
    setMessages(prev => [...prev, { text: "Listening...", isUser: false }]);
    recognitionRef.current?.start();
    setIsOpen(true);
  } else {
    // Stop listening but keep popup open
    recognitionRef.current?.stop();
  }
  setIsListening(!isListening);
}, [isListening, isProcessing]);

  return (
  <Box
  ref={containerRef}
  sx={{
    position: 'fixed',
    bottom: 100,
    right: 24,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2
  }}
>
   <Fade 
  in={isOpen}
  
>
  <Paper
    elevation={3}
    onClick={(e) => e.stopPropagation()}
    sx={{
      position: 'absolute',
      bottom: '100%',
      right: 0,
      mb: 2,
      p: 2,
      width: 280,
      maxHeight: 300,
      overflowY: 'auto',
      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper',
      borderRadius: 2,
      '&::-webkit-scrollbar': {
        width: '0.4em',
      },
      '&::-webkit-scrollbar-track': {
        boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
        webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.1)',
        outline: '1px solid slategrey',
        borderRadius: '4px',
      },
    }}
  >
        {messages.map((msg, index) => (
          <Box
            key={index}
            sx={{
              textAlign: msg.isUser ? 'right' : 'left',
              mb: 1,
              width: '100%'
            }}
          >
            <Paper
              elevation={1}
              sx={{
                display: 'inline-block',
                p: 1.5,
                borderRadius: 2,
                bgcolor: msg.isUser 
                  ? theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.light'
                  : theme.palette.mode === 'dark' ? 'grey.700' : 'grey.100',
                color: msg.isUser ? 'white' : 'text.primary',
                maxWidth: '80%',
                wordWrap: 'break-word',
              }}
            >
              {msg.text}
            </Paper>
          </Box>
        ))}
        {isListening && (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Box 
              component="span" 
              sx={{ 
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                animation: 'pulse 1.5s infinite',
                margin: '0 2px',
                '&:nth-of-type(2)': { animationDelay: '0.2s' },
                '&:nth-of-type(3)': { animationDelay: '0.4s' },
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 0.3, transform: 'translateY(0)' },
                  '50%': { opacity: 1, transform: 'translateY(-5px)' },
                }
              }} 
            />
          </Box>
        )}
      </Paper>
    </Fade>

<IconButton
  onClick={(e) => {
    e.stopPropagation();
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      if (!isOpen) {
        setMessages(prev => [...prev, { text: "Listening...", isUser: false }]);
      }
      recognitionRef.current?.start();
      setIsOpen(true);
      setIsListening(true);
    }
  }}
  onMouseDown={(e) => e.stopPropagation()}
  sx={{
    width: 60,
    height: 60,
    bgcolor: isOpen ? 'error.main' : 'primary.main',  // Red when open, blue when closed
    color: 'white',
    '&:hover': {
      bgcolor: isOpen ? 'error.dark' : 'primary.dark',
    },
    transition: 'all 0.3s ease',
    boxShadow: theme.shadows[4],
    ...(isListening && {
      animation: 'pulse 1.5s infinite',
      '@keyframes pulse': {
        '0%': { transform: 'scale(1)' },
        '50%': { transform: 'scale(1.05)' },
        '100%': { transform: 'scale(1)' }
      }
    })
  }}
>
  {isListening ? <MicOffIcon /> : <MicIcon />}
</IconButton>

  </Box>
);
};

export default VoiceAssistant;